import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface TrainingVideo {
  id: string;
  title: string;
  video_path: string;
  thumbnail_path?: string | null;
}

interface TrainingVideoDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  video: TrainingVideo | null;
  onSuccess: () => void;
}

export function TrainingVideoDeleteDialog({
  open,
  onOpenChange,
  video,
  onSuccess,
}: TrainingVideoDeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!video) return;

    setDeleting(true);
    try {
      // Delete video file from storage
      const { error: videoStorageError } = await supabase.storage
        .from('training-videos')
        .remove([video.video_path]);

      if (videoStorageError) {
        console.error('Error deleting video file:', videoStorageError);
      }

      // Delete thumbnail if exists
      if (video.thumbnail_path) {
        const { error: thumbStorageError } = await supabase.storage
          .from('training-videos')
          .remove([video.thumbnail_path]);

        if (thumbStorageError) {
          console.error('Error deleting thumbnail:', thumbStorageError);
        }
      }

      // Delete associated documents from storage
      const { data: docs } = await supabase
        .from('training_video_documents')
        .select('file_path')
        .eq('video_id', video.id);

      if (docs && docs.length > 0) {
        const docPaths = docs.map(d => d.file_path);
        await supabase.storage.from('training-videos').remove(docPaths);
      }

      // Delete documents records
      await supabase
        .from('training_video_documents')
        .delete()
        .eq('video_id', video.id);

      // Delete completion records
      await supabase
        .from('training_video_completions')
        .delete()
        .eq('video_id', video.id);

      // Delete video record
      const { error: dbError } = await supabase
        .from('training_videos')
        .delete()
        .eq('id', video.id);

      if (dbError) throw dbError;

      toast.success("Video erfolgreich gelöscht!");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error("Fehler beim Löschen des Videos");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Video löschen?</AlertDialogTitle>
          <AlertDialogDescription>
            Möchten Sie das Video "{video?.title}" wirklich löschen? 
            Diese Aktion kann nicht rückgängig gemacht werden. 
            Alle zugehörigen Dokumente und Bestätigungen werden ebenfalls gelöscht.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird gelöscht...
              </>
            ) : (
              "Löschen"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
